import { useMsal } from '@azure/msal-react';
import msLogo from 'assets/microsoftlogo.png';
import background from 'assets/Resc_Office.png';
import { CustomInput } from 'components/UI/CustomInput';
import Toggle from 'components/UI/Toggle';
import { usePerms } from 'contexts/PermsContext';
import DOMPurify from 'dompurify';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { msalInitPromise } from 'features/Auth/contexts/utils/authUtils';
import { getPackedInquiryNotes } from 'features/Auth/utils/contactInquiry.utils';
import Cookies from 'js-cookie';
import { useContext, useEffect, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loginWithCredentials } from 'services/auth/auth.service';
import { ms_syncAllUsers } from 'services/auth/ms.service';
import { submitFusionInquiry } from 'services/user/user.service';
import { setUserEmail, setUserIdentity } from 'store/slices/User/userSlice';
import 'styles/links.css';
import { clearCookies } from 'utils/sessions/cookie.utils';
import { getLogo } from 'utils/images/image.utils';
import { resolveMessageText } from 'utils/notifications/toast.utils';
import ContactUs from './ContactUs';
import './Signin.css';

const getSignInErrorMessage = (error) => {
  const backendMessage = resolveMessageText(error?.response?.data);

  if (backendMessage === 'API DISABLED') {
    return 'License disabled, contact RescAlert to restore access.';
  }

  return backendMessage || error?.message || 'Unable to sign in. Please try again.';
};

export default function Signin({
  termsAgreed,
  setTermsAgreed,
  privacyAgreed,
  setPrivacyAgreed,
  cookiesAgreed,
  setCookiesAgreed,
  policyVersion,
  termsVersion,
  setNewTermsFullyAgreed,
  newTermsFullyAgreed,
  termsDate,
  terms,
  termsFullyViewed,
  setTermsFullyViewed,
  hasSignedIn,
  setHasSignedIn,
  setShowFull,
  termsStatus,
  policyStatus,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { login: authLogin } = useContext(AuthContext);
  const [email, setEmail] = useState(Cookies.get('email') || '');
  const [password, setPassword] = useState('');
  const perms = usePerms();
  const [display, setDisplay] = useState('login');
  const [rememberMe, setRememberMe] = useState(Cookies.get('rememberMe') === 'true');
  // ContactUs utils
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState(null);
  const isEmail = (s) => /\S+@\S+\.\S+/.test(String(s || '').trim());
  const hasMinDigits = (s, n = 7) => String(s || '').replace(/\D+/g, '').length >= n;
  //MICROSOFT LOGIN
  const { instance, inProgress } = useMsal();
  const sanitizedTermsToRender = DOMPurify.sanitize(terms);

  useEffect(() => {
    perms.reset();
  }, []);

  const [contactName, setContactName] = useState('');
  const [contactOrganizationName, setContactOrganizationName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactReason, setContactReason] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactNotes, setContactNotes] = useState('');
  const [signInProvider, setSignInProvider] = useState(null);
  const hasRequiredContactFields = [
    contactName,
    contactOrganizationName,
    contactEmail,
    contactPhone,
    contactReason,
  ].every((value) => value.trim().length > 0);

  useEffect(() => {
    if (Cookies.get('rememberMe') === 'true') {
      setEmail(email);
      setRememberMe(true);

      if (Cookies.get('policyAgreed') === policyVersion) {
        setPrivacyAgreed(true);
        setCookiesAgreed(true);
      }
      if (Cookies.get('termsAgreed') === termsVersion) setTermsAgreed(true);
    } else {
      if (Cookies.get('rememberMe') === 'false') dispatch(setUserEmail(''));
      setEmail('');
      setPassword('');
      setRememberMe(false);
    }
  }, []);
  // const [isAccountActive, setIsAccountActive] = useState(false);

  const onMicrosoftSignIn = async () => {
    if (!instance) {
      toast.error('Microsoft login is not configured', { toastId: 'signin-error' });
      return;
    }

    try {
      // Wait for MSAL to be fully initialized (required in v3.x)
      await msalInitPromise;

      // Call loginRedirect with scopes
      await instance.loginRedirect({
        scopes: ['User.Read'],
      });
    } catch (error) {
      console.error('[MSAL] loginRedirect failed:', error);
      const errorMessage = error?.message || 'Microsoft login failed';
      toast.error(errorMessage, { toastId: 'signin-error' });
    }
  };

  const doNewSignIn = (provider) => {
    if (!provider && !(password && email)) {
      return;
    }
    setHasSignedIn(true);
    setSignInProvider(provider);
  };

  const moveToDashboard = () => {
    //change to switch if we implement more providers
    if (signInProvider === 'microsoft-azure') {
      (async () => {
        try {
          const res = await ms_syncAllUsers(instance);
          console.debug('[Signin] ms_syncAllUsers response', res?.data);
        } catch (err) {
          console.error('[Signin] ms_syncAllUsers error', err);
        }
      })();
    }
    navigate('/dashboard', { replace: true }); // replace prevents going back to sign-in with browser back button
  };

  const onSignIn = () => {
    perms.reset();
    try {
      if (
        Cookies.get('rememberMe') === 'true' ||
        Cookies.get('rememberMe') === true ||
        rememberMe === true
      ) {
        dispatch(setUserEmail(email));
        Cookies.set('rememberMe', rememberMe, { expires: 365 });

        Cookies.set('policyAgreed', policyVersion, { expires: 365 });
        Cookies.set('termsAgreed', termsVersion, { expires: 365 });
      } else {
        if (Cookies.get('rememberMe') === 'false') {
          dispatch(setUserEmail(''));
          Cookies.remove('rememberMe');
          Cookies.remove('policyAgreed');
          Cookies.remove('termsAgreed');
        }
      }
      loginWithCredentials(
        email,
        password,
        termsAgreed && privacyAgreed && cookiesAgreed,
        termsVersion,
        policyVersion,
      )
        .then((res) => {
          const { userName, organization } = res.data;
          if (res.data.message === 'User authenticated') {
            // Signal login success — tokens are httpOnly cookies set by the server
            authLogin();
            dispatch(setUserIdentity({ email, username: userName, organization }));
            moveToDashboard();
          }
        })
        .catch((err) => {
          const errorMessage = getSignInErrorMessage(err);
          toast.error(errorMessage, { toastId: 'signin-error' });
          setHasSignedIn(false);
          setTermsFullyViewed(false);
        });
    } catch (err) {}
  };

  useEffect(() => {
    setTermsFullyViewed(false);
    setNewTermsFullyAgreed(false);
  }, []);

  const onContactUs = async () => {
    if (contactSubmitting || !hasRequiredContactFields) {
      return;
    }
    if (!isEmail(contactEmail)) {
      setContactError('Please enter a valid email.');
      return;
    }
    if (!hasMinDigits(contactPhone, 7)) {
      setContactError('Please enter a valid phone.');
      return;
    }

    setContactError(null);
    setContactSubmitting(true);

    try {
      // The inquiry API accepts name, email, phone and notes; preserve the new details in notes.
      await submitFusionInquiry(
        contactName.trim(),
        contactEmail.trim(),
        contactPhone.trim(),
        getPackedInquiryNotes({
          organizationName: contactOrganizationName,
          reason: contactReason,
          notes: contactNotes,
        }),
      );
      setContactSuccess(true);
      // reset fields
      setContactNotes('');
    } catch (err) {
      setContactError(err?.response?.data?.message || 'Failed to send. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const onRememberToggle = (val) => {
    setRememberMe(val);
    if (!val) {
      if (Cookies.get('rememberMe') === 'false') dispatch(setUserEmail(''));
    }
    // setRememberMe(!rememberMe);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Check if the content height is less than or equal to the container height
    if (scrollHeight <= clientHeight) {
      setTermsFullyViewed(true);
    } else if (scrollTop + clientHeight >= scrollHeight) {
      setTermsFullyViewed(true);
    }
  };

  /////////////////////////////
  // Post Sign-In Terms Page //
  /////////////////////////////

  if (!newTermsFullyAgreed && hasSignedIn) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center p-2 position-relative text-center">
        <img
          src={getLogo(true)}
          alt="Logo"
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            maxWidth: 200,
            height: 'auto',
          }}
        />
        <h1>Terms of Use</h1>
        <p>This Terms of Use Policy was last modified on {termsDate}</p>
        <div
          className="overflow-auto d-flex mt-4 p-3 flex-column customScrollbar-Y"
          style={{
            background: 'var(--color-primary-shadow)',
            whiteSpace: 'pre-wrap',
            minHeight: '250px',
            maxHeight: '30vi',
            width: '90%',
          }}
          onScroll={handleScroll}
        >
          <div
            className="html-content"
            dangerouslySetInnerHTML={{ __html: sanitizedTermsToRender }}
          ></div>
        </div>
        {!termsFullyViewed && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 20,
              marginTop: 20,
            }}
          >
            <input
              type="checkbox"
              value={termsFullyViewed}
              onChange={(e) => {
                setTermsFullyViewed(e.target.checked);
              }}
            />
            I have read the Terms of Use
          </div>
        )}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button
              style={{
                backgroundColor: 'var(--color-background)',
                border: '1px solid white',
                color: 'var(--color-font)',
                padding: 10,
                margin: 10,
                marginLeft: 0,
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                clearCookies();
                setNewTermsFullyAgreed(false);
                setTermsFullyViewed(false);
                setPrivacyAgreed(false);
                setCookiesAgreed(false);
                setHasSignedIn(false);
                setShowFull(true);
              }}
            >
              Decline
            </button>
            <button
              style={{
                backgroundColor: termsFullyViewed ? '#ffffff' : '#777777',
                border: '1px solid #0b1826',
                padding: 10,
                margin: 10,
                cursor: termsFullyViewed ? 'pointer' : 'not-allowed',
                color: 'var(--color-background)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (privacyAgreed && cookiesAgreed) {
                  if (signInProvider === 'microsoft-azure') {
                    onMicrosoftSignIn();
                  } else {
                    onSignIn();
                  }
                } else {
                  alert('Please accept the Privacy & Cookie Policy');
                }
              }}
            >
              Accept Terms of Use
            </button>
          </div>
        </div>
      </div>
    );
  }

  ////////////////////////////
  // Terms Missing Toast //
  ////////////////////////////

  // Show a toast error if the terms or policy failed to return 200
  console.log('termsStatus:', termsStatus, 'policyStatus:', policyStatus);
  if (
    (termsStatus !== undefined && termsStatus !== 200) ||
    (policyStatus !== undefined && policyStatus !== 200)
  ) {
    toast.error(
      <div>
        Error loading Terms of Use and Privacy Policy. Please refresh and Try Again. If the issue
        persists, contact RescAlert support at
        {/* #0dcaf0 #0078d7 */}
        <a href="mailto:level3.support@overwatch.ai" className="link">
          {' '}
          level3.support@overwatch.ai
        </a>
      </div>,
      {
        toastId: 'signin-error',
      },
    );
    setHasSignedIn(false);
  }

  ///////////////
  // Sign-In  //
  //////////////
  return (
    <div>
      <Container fluid className="position-fixed top-0 bottom-0 start-0 end-0 overflow-y-auto p-0">
        {/* At least the fixed container's height, so both columns fill a tall screen; the
            container scrolls when the form is taller. 100% follows the visible viewport, which
            100vh does not on mobile browsers. */}
        <div className="flex-box row g-0 position-relative" style={{ minHeight: '100%' }}>
          <div
            className="col-12 col-md-6 d-none d-md-flex align-items-end flex-grow-1 p-0"
            style={{
              backgroundImage: `url(${background})`,
              backgroundSize: 'cover',
              backgroundPosition: '60% 100%',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <div className="row g-0 w-100 text-center">
              <div
                className="mb-5"
                style={{
                  WebkitTextStroke: '0.3px black',
                  color: 'var(--color-font)',
                }}
              >
                <div style={{ fontSize: 20 }}>Overwatch Fusion+ </div>
                <div style={{ fontSize: 36 }}>Powered by RescAlert Inc.</div>
              </div>
            </div>
          </div>

          <div
            className="col-12 col-md-6 d-flex flex-column align-items-center justify-content-between gap-1 py-2 px-0"
            style={{ background: 'var(--color-background)', color: 'var(--color-font)' }}
          >
            {/* Logo */}
            <img className="signin-logo w-75 h-auto flex-shrink-0" src={getLogo(true)} alt="Logo" />

            {/* Form */}
            <div className="w-100 px-3" style={{ maxWidth: 380 }}>
              {display === 'login' ? (
                <>
                  <div className="mb-1">
                    <h1 className="mb-2">Nice to see you!</h1>
                    <div style={{ color: '#A0AEC0' }}>Enter your email and password to sign in</div>
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!newTermsFullyAgreed) {
                        doNewSignIn(null);
                        return;
                      }
                      if (privacyAgreed && cookiesAgreed) onSignIn();
                      else alert('Please accept the Privacy & Cookie Policy');
                    }}
                  >
                    <div className="mb-2">
                      <div className="mx-2">Email</div>
                      <CustomInput
                        value={email}
                        callback={setEmail}
                        type="text"
                        placeholder="Your email address"
                        autoComplete="email"
                      />
                    </div>

                    <div className="mb-2">
                      <div className="mx-2">Password</div>
                      <CustomInput
                        value={password}
                        callback={setPassword}
                        type="password"
                        placeholder="Your password"
                        autoComplete="current-password"
                      />
                    </div>

                    <div className="flex-row align-items-center">
                      <Toggle callback={onRememberToggle} value={rememberMe} />
                    </div>

                    {(!privacyAgreed || !cookiesAgreed) && (
                      <div className="mt-2 text-warning small" role="alert">
                        Please accept the Privacy & Cookie Policy below to sign in
                      </div>
                    )}

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 rounded-4 mt-2 py-2"
                      disabled={
                        !email ||
                        !password ||
                        !privacyAgreed ||
                        !cookiesAgreed ||
                        termsStatus !== 200 ||
                        policyStatus !== 200
                      }
                    >
                      SIGN IN
                    </Button>
                  </form>

                  <div className="text-center mt-2">
                    <a
                      href="/forgotpassword"
                      style={{
                        color: 'var(--color-font)',
                        textDecoration: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      Forgot Password?
                    </a>
                  </div>

                  {/* MS sign-in stays only on login */}
                  <div className="text-center mt-2">
                    <div className="fs-5">Sign in with</div>
                    <div className="d-flex d-inline-flex w-50 align-items-center flex-nowrap">
                      <Button
                        variant="light"
                        className="flex-fill align-items-center gap-2 rounded-4 shadow-sm px-4 py-2 text-dark"
                        onClick={() => {
                          setSignInProvider('microsoft-azure');
                          if (!newTermsFullyAgreed) {
                            setHasSignedIn(true);
                            return;
                          }
                          if (privacyAgreed && cookiesAgreed) {
                            onMicrosoftSignIn();
                          } else {
                            setHasSignedIn(true);
                          }
                        }}
                        aria-label="Sign in with Microsoft"
                        disabled={inProgress !== 'none' || !privacyAgreed || !cookiesAgreed}
                      >
                        <img src={msLogo} alt="Microsoft" width={28} height={28} />
                        <span className="fw-medium text-nowrap">Microsoft</span>
                      </Button>
                    </div>
                  </div>
                  <div className="w-100 py-1 text-center">
                    <span style={{ color: '#A0AEC0' }}>Don't have an account? </span>
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none px-0"
                      style={{ color: 'var(--color-font)', cursor: 'pointer' }}
                      onClick={() => setDisplay('contact')}
                    >
                      Contact Us
                    </button>
                  </div>
                </>
              ) : (
                <ContactUs
                  contactName={contactName}
                  setContactName={setContactName}
                  contactOrganizationName={contactOrganizationName}
                  setContactOrganizationName={setContactOrganizationName}
                  contactEmail={contactEmail}
                  setContactEmail={setContactEmail}
                  contactPhone={contactPhone}
                  setContactPhone={setContactPhone}
                  contactReason={contactReason}
                  setContactReason={setContactReason}
                  contactNotes={contactNotes}
                  setContactNotes={setContactNotes}
                  success={contactSuccess}
                  error={contactError}
                  submitting={contactSubmitting}
                  hasRequiredFields={hasRequiredContactFields}
                  onSubmit={onContactUs}
                  onBack={() => {
                    setContactSuccess(false);
                    setContactError(null);
                    setDisplay('login');
                  }}
                />
              )}
            </div>
            <footer className="w-100 text-center">
              <small>© 2023 – {new Date().getFullYear()}, Powered by RescAlert Inc.</small>
              <div className="d-flex justify-content-center flex-wrap gap-4">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none px-0"
                  style={{ color: 'var(--color-font)', opacity: 0.6 }}
                  onClick={() => {}}
                >
                  Marketplace
                </button>

                <button
                  type="button"
                  className="btn btn-link text-decoration-none px-0"
                  style={{ color: 'var(--color-font)', opacity: 0.6 }}
                  onClick={() => {}}
                >
                  Blog
                </button>

                <button
                  type="button"
                  className="btn btn-link text-decoration-none px-0"
                  style={{ color: 'var(--color-font)', opacity: 0.6 }}
                  onClick={() => {}}
                >
                  License
                </button>
              </div>
            </footer>
          </div>
        </div>
      </Container>
    </div>
  );
}
